import React, { useState } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  LabelEntityType,
  useCreateLabelMutation,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import CourieButton from "@src/common/components/Button";

interface LabelCreationProps {
  onLabelCreated?: (label: any) => void;
  disabled?: boolean;
  entityType: LabelEntityType;
}

export function LabelCreation({
  onLabelCreated,
  disabled = false,
  entityType,
}: LabelCreationProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [labelName, setLabelName] = useState("");

  const { courier } = useAuth();
  const courierId = courier?.courier?.id;
  const showError = useErrorToast();

  const [createLabelMutation, { loading: isCreatingLabel }] =
    useCreateLabelMutation({
      onError: (error) => {
        showError(error);
      },
      onCompleted: (res) => {
        setIsCreating(false);
        setLabelName("");
        if (onLabelCreated) {
          onLabelCreated(res.createLabel);
        }
      },
    });

  const handleCreateLabel = () => {
    if (!courierId || !labelName.trim()) return;

    createLabelMutation({
      variables: {
        courierId,
        entityType: entityType,
        name: labelName.trim(),
      },
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setLabelName("");
  };

  if (isCreating) {
    return (
      <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/50">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
              Label Name
            </label>
            <input
              type="text"
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              placeholder="Enter label name"
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              disabled={isCreatingLabel}
              autoFocus
            />
          </div>

          <div className="flex justify-end space-x-2">
            <CourieButton
              size="xs"
              color="gray"
              onClick={handleCancel}
              disabled={isCreatingLabel}
            >
              Cancel
            </CourieButton>
            <CourieButton
              size="xs"
              onClick={handleCreateLabel}
              disabled={isCreatingLabel || !labelName.trim()}
            >
              {isCreatingLabel ? "Creating..." : "Create Label"}
            </CourieButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CourieButton
      size="xs"
      onClick={() => setIsCreating(true)}
      disabled={disabled}
    >
      <PlusIcon className="w-4 h-4 mr-1" />
      Create New Label
    </CourieButton>
  );
}
